import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  message,
  Spin,
  Tag,
  Space,
  Typography,
  Card,
  Input,
  Row,
  Col,
  Drawer,
  Skeleton,
  Tooltip,
} from 'antd';
import {
  SearchOutlined, ClearOutlined, HistoryOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';
import 'moment/locale/ru';
import TicketHistory from './TicketHistory';
import { statusType, fetchTicketHistory, restoreTicket } from './utils';

// Устанавливаем русскую локаль для moment
moment.locale('ru');

function TicketRestore() {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTicketId, setSearchTicketId] = useState('');
  const [searchTicketNumber, setSearchTicketNumber] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [currentTicket, setCurrentTicket] = useState({});
  const [historyData, setHistoryData] = useState([]);
  const { Title, Text } = Typography;

  // Получение данных с сервера
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Получаем токен из localStorage
        const token = localStorage.getItem('token');

        if (!token) {
          message.error('Токен авторизации не найден');
          setLoading(false);
          return;
        }

        // Делаем запрос к API
        const response = await axios.post(`/server/api/ignoretickets/?action=get&token=${token}`);

        // Проверяем успешность запроса
        if (response.data.tickets_ignore && response.data.status === 'ok') {
          // Преобразуем данные для таблицы
          const formattedData = response.data.tickets_ignore.map((item, index) => {
            // Получаем текстовое название статуса из объекта statusType
            const statusText = statusType[item.old_status] || `Неизвестный статус (${item.old_status})`;

            return {
              key: index.toString(),
              ticket_id: item.ticket_id,
              ticket_number: item.ticket_number,
              added_by: item.created_by_user,
              added_date: item.created_on,
              formatted_date: moment(item.created_on).format('DD MMMM YYYY, HH:mm'),
              old_status: item.old_status,
              status_text: statusText,
              comment: item.comment,
            };
          });

          setData(formattedData);
          setFilteredData(formattedData);
        } else {
          message.error(response.data.message || 'Ошибка при получении данных');
        }
      } catch (error) {
        // Логируем ошибку в консоль только в режиме разработки
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.error('Ошибка при получении данных:', error);
        }
        message.error('Не удалось загрузить данные о талонах');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const showTicketHistory = async (record) => {
    try {
      setCurrentTicket(record);
      setDrawerOpen(true);
      setDrawerLoading(true);
      const result = await fetchTicketHistory(record.ticket_id);
      if (result.success) {
        setHistoryData(result.data);
      } else {
        message.error(result.message || 'Ошибка при получении истории талона');
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('Ошибка при отображении истории талона:', error);
      }
      message.error('Не удалось загрузить историю талона');
    } finally {
      setDrawerLoading(false);
    }
  };

  // Функция для восстановления талона
  const handleRestore = async (ticketId) => {
    try {
      const result = await restoreTicket(ticketId, setLoading, data, setData, setFilteredData);
      if (result.success) {
        message.success(result.message);
      }
    } catch (error) {
      message.error('Не удалось восстановить талон');
    }
  };

  // Определение колонок таблицы
  const columns = [
    {
      title: 'ID талона',
      dataIndex: 'ticket_id',
      key: 'ticket_id',
      sorter: (a, b) => a.ticket_id - b.ticket_id,
    },
    {
      title: 'Номер талона',
      dataIndex: 'ticket_number',
      key: 'ticket_number',
      sorter: (a, b) => a.ticket_number.localeCompare(b.ticket_number),
    },
    {
      title: 'Кто перевел в неучтенный',
      dataIndex: 'added_by',
      key: 'added_by',
    },
    {
      title: 'Когда',
      dataIndex: 'formatted_date',
      key: 'added_date',
      sorter: (a, b) => moment(a.added_date).unix() - moment(b.added_date).unix(),
      render: (text) => text,
    },
    {
      title: 'Старый статус',
      dataIndex: 'status_text',
      key: 'old_status',
      ellipsis: true,
      width: 300,
      filters: Object.values(statusType).map((value) => ({ text: value, value })),
      onFilter: (value, record) => record.status_text === value,
      render: (text, record) => {
        // Определяем цвет тега в зависимости от статуса
        let color = 'default';
        switch (record.old_status) {
          case '1': color = 'blue'; break;
          case '2': color = 'green'; break;
          case '3': color = 'cyan'; break;
          case '4': color = 'purple'; break;
          case '5': color = 'red'; break;
          case '6': color = 'orange'; break;
          case '7': color = 'gold'; break;
          case '8': color = 'lime'; break;
          case '9': color = 'magenta'; break;
          default: color = 'default';
        }

        return (
          <Tooltip title={`Код статуса: ${record.old_status}`}>
            <Tag color={color}>{text}</Tag>
          </Tooltip>
        );
      },
    },
    {
      title: 'Комментарий',
      dataIndex: 'comment',
      key: 'comment',
    },
    {
      title: 'Действия',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            onClick={() => handleRestore(record.ticket_id)}
          >
            Восстановить
          </Button>
          <Button
            type="default"
            icon={<HistoryOutlined />}
            onClick={() => showTicketHistory(record)}
          >
            История
          </Button>
        </Space>
      ),
    },
  ];

  // Функция для поиска по ID и номеру талона
  const handleSearch = () => {
    const filtered = data.filter((item) => {
      const idMatch = searchTicketId ? item.ticket_id.toString().includes(searchTicketId) : true;
      const numberMatch = searchTicketNumber ? item.ticket_number.includes(searchTicketNumber) : true;
      return idMatch && numberMatch;
    });
    setFilteredData(filtered);
  };

  // Функция для очистки поиска
  const clearSearch = () => {
    setSearchTicketId('');
    setSearchTicketNumber('');
    setFilteredData(data);
  };

  // Функция для отображения истории талона

  // Функция для закрытия Drawer
  const onDrawerClose = () => {
    setDrawerOpen(false);
    setHistoryData([]);
  };

  return (
    <div style={{ padding: '20px' }}>
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px',
        }}
        >
          <Title level={2}>Восстановление талонов</Title>
        </div>

        <Card style={{ marginBottom: '16px' }}>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={8} md={6} lg={5}>
              <Input
                placeholder="Поиск по ID талона"
                value={searchTicketId}
                onChange={(e) => setSearchTicketId(e.target.value)}
                prefix={<SearchOutlined />}
                allowClear
              />
            </Col>
            <Col xs={24} sm={8} md={6} lg={5}>
              <Input
                placeholder="Поиск по номеру талона"
                value={searchTicketNumber}
                onChange={(e) => setSearchTicketNumber(e.target.value)}
                prefix={<SearchOutlined />}
                allowClear
              />
            </Col>
            <Col>
              <Space>
                <Button type="primary" onClick={handleSearch} icon={<SearchOutlined />}>
                  Найти
                </Button>
                <Button onClick={clearSearch} icon={<ClearOutlined />}>
                  Сбросить
                </Button>
              </Space>
            </Col>
            <Col flex="auto" style={{ textAlign: 'right' }}>
              {filteredData.length !== data.length && (
                <Text type="secondary">
                  Отображено
                  {' '}
                  {filteredData.length}
                  {' '}
                  из
                  {' '}
                  {data.length}
                  {' '}
                  записей
                </Text>
              )}
            </Col>
          </Row>
        </Card>
        <Card>
          {loading && (
            <div style={{ textAlign: 'center', margin: '50px 0' }}>
              <Spin size="large" tip="Загрузка данных..." />
            </div>
          )}
          {!loading && data.length === 0 && (
            <div style={{ textAlign: 'center', margin: '50px 0' }}>
              <Text type="secondary" style={{ fontSize: '16px' }}>Нет данных для отображения</Text>
            </div>
          )}
          {!loading && data.length > 0 && (
            <Table
              columns={columns}
              dataSource={filteredData}
              pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
              rowKey="ticket_id"
              bordered
              size="middle"
              scroll={{ x: 'max-content' }}
            />
          )}
        </Card>

      </Space>

      {/* Drawer для отображения истории талона */}
      <Drawer
        width={840}
        title={`История талона № ${currentTicket.ticket_number || ''}`}
        placement="right"
        onClose={onDrawerClose}
        open={drawerOpen}
      >
        {drawerLoading ? <Skeleton active /> : (
          <TicketHistory
            historyData={historyData}
            currentTicket={currentTicket}
          />
        )}
      </Drawer>
    </div>
  );
}

export default TicketRestore;
